import { apiClient } from './client'

// Token Savings Estimator (Task 8): re-exported here so the Observability
// page has one import surface, per plan Task 8's "extend
// frontend/src/api/observability.ts with typed token-savings contracts".
// The implementation lives in ./token-savings.ts, shared with the
// dashboard/project panels (Task 7) so the two surfaces cannot diverge.
export {
  getSettingsTokenSavings,
  getTokenSavingsFacets,
  TOKEN_SAVINGS_PERIODS,
  type TokenSavingsBreakdownRow,
  type TokenSavingsFacets,
  type TokenSavingsQuery,
  type TokenSavingsReport,
  type TokenSavingsReportBucket,
} from './token-savings'

export function getObservabilitySummary() {
  return apiClient.get<ObservabilitySummaryResponse>('/settings/observability/summary')
}

export function getObservabilityClients() {
  return apiClient.get<{ clients: ObservabilityClient[] }>('/settings/observability/clients')
}

export function getObservabilityTraces(params?: {
  limit?: number
  status?: string
  errorClass?: string
  diagnosticCode?: string
  requestId?: string
  tool?: string
  days?: number
}) {
  const q = new URLSearchParams()
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.status) q.set('status', params.status)
  if (params?.errorClass) q.set('errorClass', params.errorClass)
  if (params?.diagnosticCode) q.set('diagnosticCode', params.diagnosticCode)
  if (params?.requestId) q.set('requestId', params.requestId)
  if (params?.tool) q.set('tool', params.tool)
  if (params?.days) q.set('days', String(params.days))
  const qs = q.toString()
  return apiClient.get<{ traces: Record<string, unknown>[] }>(
    `/settings/observability/traces${qs ? `?${qs}` : ''}`
  )
}

export function getObservabilityAccess(params?: { limit?: number; days?: number }) {
  const q = new URLSearchParams()
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.days) q.set('days', String(params.days))
  const qs = q.toString()
  return apiClient.get<{ access: Record<string, unknown>[] }>(
    `/settings/observability/access${qs ? `?${qs}` : ''}`
  )
}

export function getObservabilityFreshness() {
  return apiClient.get<{ projects: ObservabilityFreshness[] }>('/settings/observability/freshness')
}

export function getObservabilityEvals() {
  return apiClient.get<{ summaries: Record<string, unknown>[]; running: boolean }>(
    '/settings/observability/evals'
  )
}

export function getErrorHealth(days = 14) {
  return apiClient.get<{ diagnostics: DiagnosticHealth[]; days: number }>(
    `/settings/observability/error-health?days=${days}`
  )
}

export function getPlaybooks(params?: { errorClass?: string; code?: string }) {
  const q = new URLSearchParams()
  if (params?.errorClass) q.set('errorClass', params.errorClass)
  if (params?.code) q.set('code', params.code)
  const qs = q.toString()
  return apiClient.get<{ playbooks: RunbookEntry[] }>(
    `/settings/observability/playbooks${qs ? `?${qs}` : ''}`
  )
}

export interface DiagnosticHealth {
  diagnosticCode: string
  errorClass: string
  countWindow: number
  countLifetime: number
  lastFailureAt: string | null
  lastRecoveryAt: string | null
  status: 'active' | 'resolved' | 'quiet'
  reopenCount: number
  affectedProjects: number
  affectedClients: number
  affectedTools: number
  severity: 'user_facing' | 'degraded' | 'ops'
}

export interface RunbookEntry {
  errorClass: string
  diagnosticCode?: string
  title: string
  severity: 'user_facing' | 'degraded' | 'ops'
  symptoms: string[]
  impactSummary: string
  checks: string[]
  resolutionSteps: string[]
  verifySteps: string[]
  escalateWhen: string[]
  links: Array<{ label: string; href: string }>
}

export function runObservabilityEval(body?: { projectName?: string; goldSetPath?: string }) {
  return apiClient.post<{
    runId: string
    projectName: string
    queryCount: number
    recallAt5: number
    recallAt10: number
    mrr: number
    finishedAt: string
  }>('/settings/observability/evals/run', body ?? {})
}

export interface ObservabilitySummaryResponse {
  live: {
    inFlight: number
    inFlightMax: number
    httpTotal: number
    httpErrors: number
    mcpHttpTotal: number
    mcpHttpErrors: number
    mcpToolTotal: number
    mcpToolErrors: number
  }
  latency: { p50: number | null; p95: number | null; p99: number | null }
  errorClasses: Array<{ errorClass: string; transport: string; count: number }>
  observability: {
    enabled: boolean
    dir: string
    retentionDays: number
    maxDiskMb: number
    successSampleRate: number
  } | null
}

export interface ObservabilityClient {
  keyId: number
  keyName: string
  lastUsedAt: string | null
  userId: number | null
  httpCalls: number
  httpErrors: number
  toolCalls: number
  toolErrors: number
  tools: Array<{ tool: string; ok: number; errors: number }>
}

export interface ObservabilityFreshness {
  projectId: number
  projectName: string
  lastIndexedAt: string | null
  lagMs: number | null
  fileCount: number
  indexedCount: number
  pendingCount: number
}
